import { ErrorMessage } from "@hookform/error-message";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import React, { Suspense, useId } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import { getHarvestYearFromQuarter, getThisYear } from "@/common/utils/trading";
import { Currency } from "@/routes/shared/currency.params";
import { futureQuartersQuery } from "@/routes/trades/trades.api";

import { InputField } from "../form/input-field/input-field";
import { Failed } from "../info/info";
import { Spinner } from "../spinner/spinner";

/*
type WithLowPrice = {
  hasLowPrice: true;
  lowPricePerTon: number;
};
type WithoutLowPrice = {
  hasLowPrice: false;
  lowPricePerTon?: never;
};
*/

type Fields = {
  quarter: string;
  pricePerTon: number;
  // FIXME `hasLowPrice` with `lowPricePerTon` should be a union type of WithLowPrice | WithoutLowPrice,
  // but somehow, `DefaultValues` of react-hook-form infers `hasLowPrice` as either true or false, not boolean
  hasLowPrice: boolean;
  lowPricePerTon: number | null;
  trader: string;
  tons: number;
  customTransport: boolean;
  customTransportNote: string | null;
  note?: string;
  harvestYear: number;
};

const OrderForm = <TFields extends FieldValues = Fields>({
  children,
  initialValues,
  onSubmit,
}: React.PropsWithChildren<{
  onSubmit: (data: TFields) => void;
  initialValues: DefaultValues<TFields>;
}>) => {
  const form = useForm<TFields>({
    defaultValues: initialValues,
  });

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-y-6"
        id={id}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <ErrorBoundary fallbackRender={({ error }) => <Failed error={error} />}>
          <Suspense fallback={<Spinner />}>{children}</Suspense>
        </ErrorBoundary>
      </form>
    </FormProvider>
  );
};

const CustomTransportField = () => {
  const form = useFormContext<Fields>();
  const hasCustomTransport = form.watch("customTransport");

  React.useEffect(() => {
    if (!hasCustomTransport) {
      form.setValue("customTransportNote", null);
    }
  }, [form, hasCustomTransport]);

  return (
    <>
      <label
        className="relative flex h-6 justify-between"
        htmlFor="custom-transport"
      >
        <Trans>Vlastní doprava</Trans>
        <input
          {...form.register("customTransport")}
          role="switch"
          type="checkbox"
        />
      </label>
      {hasCustomTransport ? (
        <>
          <label htmlFor="custom-transport-note">
            <Trans>Poznámka k dopravě</Trans>
            <textarea
              className="mt-2 w-full rounded-lg"
              {...form.register("customTransportNote")}
            />
          </label>
        </>
      ) : null}
    </>
  );
};

const NoteField = () => {
  const form = useFormContext<Fields>();
  const id = useId();

  return (
    <label htmlFor={`note-${id}`}>
      <Trans>Poznámka</Trans>
      <textarea
        className="mt-2 w-full rounded-lg"
        rows={1}
        {...form.register("note")}
        id={`note-${id}`}
      />
    </label>
  );
};

const AmountField = () => (
  <InputField<Fields>
    autoComplete="off"
    label={<Trans>Množství v tunách</Trans>}
    name="tons"
    options={{
      min: {
        message: t`Množství musí být vyšší než 24 tun`,
        value: 24,
      },
      required: t`Vyplňte prosím množství`,
      valueAsNumber: true,
    }}
    step="0.1"
    type="number"
  />
);

const QuarterField = () => {
  const form = useFormContext<Fields>();
  const futureQuarters = useSuspenseQuery(futureQuartersQuery());
  const quarterId = form.watch("quarter");
  const quarter = futureQuarters.data.find((q) => q.id === quarterId);
  const harvestYear = quarter
    ? getHarvestYearFromQuarter(quarter.name)
    : getThisYear();

  React.useEffect(() => {
    form.setValue("harvestYear", harvestYear);
  }, [form, harvestYear]);

  return (
    <label className="group" htmlFor="quarter">
      <Trans>Kvartál</Trans>
      <select
        className="group-has-[[role=alert]]:border-can-russet mt-2 h-[calc(--spacing(14)+2px)] w-full rounded-lg p-4 font-bold"
        id="quarter"
        {...form.register("quarter", {
          required: t`Prosím vyplňte kvartál`,
        })}
      >
        {futureQuarters.data.map((quarter) => (
          <option key={quarter.id} value={quarter.id}>
            {quarter.name}
          </option>
        ))}
      </select>
      <ErrorMessage
        errors={form.formState.errors}
        name="quarter"
        render={({ message }) => (
          <p
            className="text-can-russet mt-2 basis-full text-left text-xs"
            role="alert"
          >
            {message}
          </p>
        )}
      />
    </label>
  );
};

const TraderField = ({ children }: React.PropsWithChildren) => {
  const form = useFormContext<Fields>();
  return (
    <label htmlFor="trader">
      <Trans>Obchodník</Trans>
      <select
        className="mt-2 w-full rounded-lg p-4"
        id="trader"
        required
        {...form.register("trader")}
      >
        {children}
      </select>
    </label>
  );
};

const LowPriceField = ({ currency }: { currency: Currency }) => {
  const form = useFormContext<Fields>();
  const hasLowPrice = form.watch("hasLowPrice");

  React.useEffect(() => {
    if (!hasLowPrice) {
      form.setValue("lowPricePerTon", null);
    }
  }, [hasLowPrice, form]);

  return (
    <>
      <div>
        <label
          className="relative flex h-6 justify-between"
          htmlFor="custom-transport"
        >
          <Trans>Zahrnout pokles ceny</Trans>
          <input
            {...form.register("hasLowPrice")}
            role="switch"
            type="checkbox"
          />
        </label>
        <small>
          <Trans>
            Pohlídejte si i případný pokles ceny a nastavte si minimální částku,
            za kterou dojde k prodeji komodity.
          </Trans>
        </small>
      </div>
      {hasLowPrice ? (
        <InputField<Fields>
          label={<Trans>Minimální cena za tunu ({currency})</Trans>}
          name="lowPricePerTon"
          options={{
            min: {
              message: t`Cena musí být vyšší než 0`,
              value: 1,
            },
            required: t`Vyplňte prosím cenu`,
            validate: () => {
              const lowPrice = form.getValues("lowPricePerTon");
              const price = form.getValues("pricePerTon");
              const hasLowPrice = form.getValues("hasLowPrice");
              if (!hasLowPrice) {
                return true;
              }

              if (lowPrice === null) {
                return;
              }

              return (
                lowPrice <= price ||
                t`Minimální cena musí být nižší než cena za tunu`
              );
            },
            valueAsNumber: true,
          }}
          step="0.01"
          type="number"
        />
      ) : null}
    </>
  );
};

const PriceField = ({
  currency,
  validate,
}: {
  currency: Currency;
  validate?: (value: number) => true | string;
}) => (
  <InputField<Fields>
    label={<Trans>Cena za tunu ({currency})</Trans>}
    name="pricePerTon"
    options={{
      min: {
        message: t`Cena musí být vyšší než 0`,
        value: 1,
      },
      required: t`Vyplňte prosím cenu`,
      validate: validate
        ? (value: unknown) => validate(value as number)
        : undefined,
      valueAsNumber: true,
    }}
    step="0.01"
    type="number"
  />
);
const id = "order-form";

OrderForm.formId = "order-form";
OrderForm.AmountField = AmountField;
OrderForm.CustomTransportField = CustomTransportField;
OrderForm.LowPriceField = LowPriceField;
OrderForm.NoteField = NoteField;
OrderForm.QuarterField = QuarterField;
OrderForm.PriceField = PriceField;
OrderForm.TraderField = TraderField;

export { OrderForm };
export type { Fields };
